// Customizable Area Start
import React from "react"
import { Box, Button, Input, Typography } from "@mui/material"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import { styled } from "@mui/material/styles"
import * as Yup from "yup"
import { ErrorMessage, Formik } from "formik"
import FooterController, { Props, configJSON } from "./FooterController.web"
import { bsLogo, footerBG } from "./assets"
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web"

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format!")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address!"
    )
    .required("Email is required!"),
})
// Customizable Area End

export default class Footer extends FooterController {
  constructor(props: Props) {
    super(props)
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { isLoading, isInfoDialog, infoType, infoMessage } = this.state

    return (
      <FooterWrapper>
        <Box className="footer">
          <Box className="footerContainer">
            <Box
              className="footerLogo"
              data-test-id="logo"
              onClick={this.handleHomeRedirect}
            >
              <Box component="img" src={bsLogo} alt="logo" />
            </Box>
            <Box className="footerLinksContainer">
              <Typography className="quickLinks">
                {configJSON.quickLinksText}
              </Typography>
              <Button
                data-test-id="terms-conditions"
                onClick={this.handleTermsNConditionRedirect}
              >
                Terms & Conditions
              </Button>
              <Button
                data-test-id="privacy-policy"
                onClick={this.handlePrivacyPolicyRedirect}
              >
                Privacy Policy
              </Button>
              <Button
                data-test-id="contact-us"
                onClick={this.handleContactUsRedirect}
              >
                Contact us
              </Button>
              <Button
                data-test-id="pricing"
                onClick={this.handleSubscriptionPlansRedirect}
              >
                Pricing
              </Button>
            </Box>
            <Box className="subscribeContainer">
              <Typography variant="h6" className="title">
                Subscribe
              </Typography>
              <Formik
                data-test-id="formik"
                initialValues={{
                  email: "",
                }}
                validationSchema={validationSchema}
                onSubmit={this.handleSubscribeUpdate}
              >
                {({ values, handleChange, handleSubmit }) => (
                  <form
                    noValidate
                    data-test-id="form"
                    autoComplete="false"
                    style={{ marginBottom: "20px" }}
                    onSubmit={handleSubmit}
                  >
                    <Box className="emailContainer">
                      <Input
                        name="email"
                        type="email"
                        disableUnderline
                        data-test-id="email"
                        className="emailInput"
                        value={values.email}
                        placeholder={configJSON.emailAddressText}
                        onChange={handleChange}
                      />
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="rightArrIcon"
                      >
                        <ArrowForwardRoundedIcon />
                      </Button>
                    </Box>
                    <ErrorMessage
                      className="formikError"
                      component="p"
                      name="email"
                    />
                  </form>
                )}
              </Formik>
              <Box className="stayUpdatedText" component="p">
                Stay updated! Enter your email to receive the latest news and
                announcements.
              </Box>
            </Box>
            <Box className="socialLinks">
              {this.socialMediaLinks.map((linkDetails) => {
                const { name, icon, url } = linkDetails

                return (
                  <Box title={name}>
                    <Box
                      href={url}
                      component="a"
                      target="_blank"
                      onClick={() => this.handleSocialMediaIconClick(name)}
                    >
                      <Box component="img" src={icon} alt={name} />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
          <Typography className="copyRight">
            © 2024 Bharat Stage. All rights reserved.
          </Typography>
        </Box>
        <CustomInfoDialog
          data-test-id="info-dialog"
          isOpen={isInfoDialog}
          infoType={infoType}
          title={infoMessage}
          handleClose={this.closeInfoDialog}
        />
      </FooterWrapper>
    )
    // Customizable Area End
  }
}

// Customizable Area Start
const FooterWrapper = styled("footer")({
  "& .footer": {
    backgroundColor: "#350859",
    backgroundImage: `url(${footerBG})`,
    backgroundSize: "cover",
    padding: "40px",
    "@media (max-width: 414px)": {
      padding: "20px 16px",
    },
    "@media (min-width: 415px) and (max-width: 1000px)": {
      padding: "24px",
    },
  },
  "& .footerContainer": {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: "20px",
    "@media (max-width: 414px)": {
      flexDirection: "column",
    },
  },
  "& .footerLogo": {
    height: "100px",
    width: "100px",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "20px",
    cursor: "pointer",
    "@media (max-width: 414px)": {
      height: "80px",
      width: "80px",
    },
  },
  "& .footerLogo img": {
    height: "50px",
    width: "74px",
    "@media (max-width: 414px)": {
      height: "40px",
      width: "50px",
    },
  },
  "& .footerLinksContainer": {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  "& .footerLinksContainer .quickLinks": {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "19.5px",
    color: "#fff !important",
    justifyContent: "flex-start",
    padding: 0,
    textTransform: "none",
    "@media (max-width: 414px)": {
      fontSize: "12px",
      lineHeight: "15px",
    },
    "@media (min-width: 415px) and (max-width: 1280px)": {
      fontSize: "14px",
      lineHeight: "19.5px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "18px",
      lineHeight: "21px",
    },
  },
  "& .footerLinksContainer button": {
    lineHeight: "19.5px",
    fontSize: "16px",
    color: "#fff !important",
    padding: 0,
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 500,
    "@media (max-width: 414px)": {
      lineHeight: "15px",
      fontSize: "12px",
    },
    "@media (min-width: 415px) and (max-width: 1280px)": {
      lineHeight: "19.5px",
      fontSize: "14px",
    },
    "@media (min-width: 1600px)": {
      lineHeight: "21px",
      fontSize: "18px",
    },
  },
  "& .subscribeContainer": {
    width: "466px",
    backgroundColor: "#ffffff1a",
    display: "flex",
    flexDirection: "column",
    padding: "40px",
    borderRadius: "2px",
    fontFamily: "Montserrat !important",
    "@media (max-width: 414px)": {
      width: "auto",
      padding: "16px",
    },
    "@media (min-width: 415px) and (max-width: 1000px)": {
      width: "auto",
      padding: "24px",
    },
  },
  "& .subscribeContainer p": {
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "20.4px",
    color: "#fff",
    margin: 0,
    fontFamily: "Montserrat !important",
    "@media (max-width: 600px)": {
      fontSize: "10px",
      lineHeight: "16px",
    },
    "@media (min-width: 601px) and (max-width: 1280px)": {
      fontSize: "12px",
      lineHeight: "18px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "18px",
      lineHeight: "22px",
    },
  },
  "& .title": {
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "28px",
    color: "#fff",
    marginBottom: "15px",
    "@media (max-width: 414px)": {
      fontSize: "12px",
      lineHeight: "24px",
    },
    "@media (min-width: 415px) and (max-width: 1280px)": {
      fontSize: "14px",
      lineHeight: "26px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "18px",
      lineHeight: "28px",
    },
  },
  "& .emailContainer": {
    position: "relative",
  },
  "& .emailInput": {
    backgroundColor: "#fff",
    width: "100%",
    height: "50px",
    padding: "10px 66px 10px 10px",
    borderRadius: "8px",
  },
  "& .emailInput input": {
    fontSize: "14px",
    lineHeight: "18px",
    color: "#64748b",
    "@media (max-width: 600px)": {
      fontSize: "10px",
      lineHeight: "14px",
    },
    "@media (min-width: 601px) and (max-width: 1280px)": {
      fontSize: "12px",
      lineHeight: "16px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "16px",
      lineHeight: "20px",
    },
  },
  "& .emailInput input::placeholder": {
    fontSize: "14px",
    lineHeight: "17.07px",
    color: "#64748b !important",
    fontWeight: 400,
    opacity: 1,
    "@media (max-width: 414px)": {
      fontSize: "10px",
      lineHeight: "14px",
    },
    "@media (min-width: 415px) and (max-width: 1280px)": {
      fontSize: "12px",
      lineHeight: "16px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "16px",
      lineHeight: "20px",
    },
  },
  "& .rightArrIcon": {
    position: "absolute",
    top: 0,
    right: 0,
    minWidth: "auto",
    width: "50px",
    height: "50px",
    backgroundColor: "#652784",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0 6px 6px 0",
  },
  "& .rightArrIcon svg": {
    fill: "#fff",
    cursor: "pointer",
  },
  "& .formikError": {
    marginTop: "3px !important",
    color: "#E11B23 !important",
    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
  },
  "& .socialLinks": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "20px",
    "@media (max-width: 1160px)": {
      flexDirection: "row",
    },
  },
  "& .socialLinks div": {
    width: "50px",
    height: "50px",
    backgroundColor: "#fff",
    borderRadius: "50px",
    boxShadow: "0px 7px 10px 0px rgba(75, 75, 75, 0.1)",
    "& > a": {
      height: "100%",
      width: "100%",
      display: "flex",
    alignItems: "center",
    justifyContent: "center",
    }
  },
  "& .socialLinks svg": {
    fill: "#652784",
    cursor: "pointer",
  },
  "& .copyright": {
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "26px",
    color: "#fff",
    marginTop: "-20px",
    "@media (max-width: 414px)": {
      fontSize: "10px",
      lineHeight: "22px",
      textAlign: "center",
      marginTop: "5px",
    },
    "@media (min-width: 415px) and (max-width: 1118px)": {
      fontSize: "12px",
      lineHeight: "24px",
      marginTop: "15px",
    },
    "@media (min-width: 1119px) and (max-width: 1600px)": {
      fontSize: "16px",
      lineHeight: "28px",
      marginTop: "-20px",
    },
  },
  "& .stayUpdatedText": {
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "20.4px",
    fontFamily: "Montserrat !important",
    color: "#fff",
    "@media (max-width: 414px)": {
      fontSize: "10px",
      lineHeight: "16px",
      textAlign: "center",
      fontFamily: "Montserrat !important",
    },
    "@media (min-width: 415px) and (max-width: 1118px)": {
      fontSize: "12px",
      lineHeight: "18px",
      fontFamily: "Montserrat !important",
    },
    "@media (min-width: 1119px) and (max-width: 1600px)": {
      fontSize: "14px",
      fontWeight: "500",
      lineHeight: "20.4px",
      fontFamily: "Montserrat !important",
    },
  },
})
// Customizable Area End
